import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FieldType } from '../enums/field-type.enum';
import { AppHelper } from '../helpers/app.helper';
import { IQueryService } from '../interfaces/query.service';
import { Client } from '../models/clients/client.model';
import { TaxonomyValue } from '../models/excel/taxonomies/taxonomy-value.model';
import { DropDownItem } from '../models/fields/dropdown-item.model';
import { TaxonomyField } from '../models/fields/taxonomy-field.model';
import { SubmitResult } from '../models/taxonomies/submit-result.model';
import { Taxonomy } from '../models/taxonomies/taxonomy.model';
import { AuthResult } from '../models/users/auth-result.model';


@Injectable()
export class QueryServiceFake implements IQueryService {
  //#region Fields

  private fakeTaxonomyValuesStorage: { [taxonomyId: number]: DropDownItem[]; } = {};

  //#endregion

  //#region C-tor

  constructor(
    private appHelper: AppHelper) { }

  //#endregion

  //#region Methods

  login(authCode: string): Observable<AuthResult> {
    const authResult = new AuthResult('admin@dbbest.com', 'stub', 'stub', 'stub', 100000);

    this.updateAuthResult(authResult);

    return of(authResult).pipe(delay(1000));
  }

  refreshAccessToken(refreshToken: string): Observable<AuthResult> {
    return this.login(undefined);
  }

  getClients(): Observable<Client[]> {
    return of([
      new Client(1, "Client with taxonomies"),
      new Client(2, "Client 2"),
      new Client(3, "Client 3"),
      new Client(4, "Very very very very very very very long client name 4"),
      new Client(5, "Client 5"),
      new Client(6, "Client 6"),
      new Client(7, "Client 7"),
      new Client(8, "Client 8"),
      new Client(9, "Client 9"),
      new Client(10, "Client 10"),
      new Client(11, "Client 11"),
      new Client(12, "Client 12"),
      new Client(13, "Client 13"),
      new Client(14, "Client 14"),
      new Client(15, "Client 15")
    ]).pipe(delay(1000));
  }

  getTaxonomies(clientId: number): Observable<Taxonomy[]> {
    if (clientId === 1) {
      return of([
        new Taxonomy(1, "All field types"),
        new Taxonomy(2, "Common fields test"),
        new Taxonomy(3, "Empty drop-down list test"),
        new Taxonomy(4, "Very very very very very very very long taxonomy name 4"),
        new Taxonomy(5, "Simple cascading"),
        new Taxonomy(7, "Unusual cascading"),
        new Taxonomy(8, "Invalid cascading"),
        new Taxonomy(9, "MDM field test"),
        new Taxonomy(10, "Simple cascading with other columns"),
        new Taxonomy(11, "Taxonomy within taxonomy test"),
        new Taxonomy(12, "Simple MDM cascading"),
        new Taxonomy(13, "Dates test"),
        new Taxonomy(14, "Taxonomy ID test"),
        new Taxonomy(15, "MDM Search test"),
        new Taxonomy(16, "Static test"),
      ]).pipe(delay(1000));
    } else {
      return of([]).pipe(delay(1000));
    }
  }

  getTaxonomyFields(taxonomyId: number): Observable<TaxonomyField[]> {
    if (taxonomyId === 1) {
      return of([
        {
          id: 10,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date field [mm-dd-yyyy]',
          field_desc: 'Date field description',
          dateFormat: 'mm-dd-yyyy',
          field_type: FieldType.Date,
          delimiter_value: '_'
        },
        {
          id: 12,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date field [mmmm dd, yyyy]',
          dateFormat: 'mmmm dd, yyyy',
          field_type: FieldType.Date,
          delimiter_value: '_'
        },
        {
          id: 11,
          character_limit: 15,
          mandatory: true,
          hidden: false,
          name: 'Text field',
          field_desc: 'Text field description',
          field_type: FieldType.Text,
          delimiter_value: '_'
        },
        {
          id: 16,
          character_limit: 5,
          mandatory: false,
          hidden: true,
          name: 'Text field (hidden)',
          field_desc: 'The value of this field must be excluded from the taxonomy value',
          field_type: FieldType.Text,
          delimiter_value: '_'
        },
        {
          id: 13,
          character_limit: 40,
          hidden: false,
          mandatory: true,
          name: 'Dropdown field',
          field_desc: 'Dropdown field description',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '1',
              name: 'One',
              code: 'C1',
              childItems: []
            },
            {
              id: '2',
              name: 'Two',
              code: 'C2',
              childItems: []
            },
            {
              id: '50',
              name: 'Three',
              code: '',
              childItems: []
            },
            {
              id: '51',
              name: 'Four',
              code: '0004',
              childItems: []
            }
          ]
        },
        {
          id: 21,
          character_limit: 40,
          hidden: false,
          mandatory: false,
          name: 'Dropdown field 2',
          field_desc: 'Dropdown field 2 description',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '23',
              name: 'New Value 1',
              code: 'NV1',
              childItems: []
            },
            {
              id: '24',
              name: 'New Value 2',
              code: 'NV2',
              childItems: []
            }
          ]
        },
        {
          id: 14,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'MDMData field',
          field_desc: 'MDMData field description',
          field_type: FieldType.MDMData,
          delimiter_value: '_'
        },
        {
          id: 15,
          taxonomy_id: 2,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Taxonomy field',
          field_desc: 'Taxonomy field description',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_'
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 2) {
      return of([
        {
          id: 11,
          character_limit: 15,
          hidden: false,
          mandatory: true,
          name: 'Text field',
          field_desc: 'Text field description',
          field_type: FieldType.Text,
          delimiter_value: '_'
        },
        {
          id: 17,
          character_limit: 5,
          hidden: false,
          mandatory: true,
          name: 'Text field 2',
          field_desc: 'Text field 2 description',
          field_type: FieldType.Text,
          delimiter_value: '_'
        },
        {
          id: 18,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field 2',
          field_desc: 'Dropdown field 2 description',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '21',
              name: 'First',
              code: 'F1',
              childItems: []
            },
            {
              id: '22',
              name: 'Second',
              code: 'F2',
              childItems: []
            }
          ]
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 3) {
      return of([
        {
          id: 19,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field Empty',
          field_desc: 'Dropdown field Empty description',
          field_type: FieldType.Dropdown,
          delimiter_value: '_'
        },
        {
          id: 31,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'MDM Dropdown field Empty',
          field_desc: 'MDM Dropdown field Empty description',
          field_type: FieldType.MDMData,
          delimiter_value: '_'
        },
        {
          id: 20,
          taxonomy_id: 1,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Taxonomy field Empty',
          field_desc: 'Taxonomy field Empty description',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_'
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 5) {
      return of([
        {
          id: 22,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #A',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '25',
              name: 'A1',
              code: 'A1',
              childItems: [
                {
                  fieldId: 23,
                  id: '27'
                },
                {
                  fieldId: 23,
                  id: '28'
                },
                {
                  fieldId: 24,
                  id: '30'
                },
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '26',
              name: 'A2',
              code: 'A2',
              childItems: [
                {
                  fieldId: 23,
                  id: '29'
                }
              ]
            }
          ]
        },
        {
          id: 23,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #B',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '27',
              name: 'B1',
              code: 'B1',
              childItems: [
                {
                  fieldId: 24,
                  id: '30'
                }
              ]
            },
            {
              id: '28',
              name: 'B2',
              code: 'B2',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '29',
              name: 'B3',
              code: 'B3',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            }
          ]
        },
        {
          id: 24,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #C',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '30',
              name: 'C1',
              code: 'C1',
              childItems: []
            },
            {
              id: '31',
              name: 'C2',
              code: 'C2',
              childItems: []
            }
          ]
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 7) {
      return of([
        {
          id: 27,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #A',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '36',
              name: 'A1',
              code: 'A1',
              childItems: [
                {
                  fieldId: 28,
                  id: '38'
                },
                {
                  fieldId: 31,
                  id: '44'
                },
                {
                  fieldId: 32,
                  id: '46'
                }
              ]
            },
            {
              id: '37',
              name: 'A2',
              code: 'A2',
              childItems: [
                {
                  fieldId: 28,
                  id: '39'
                },
                {
                  fieldId: 31,
                  id: '45'
                },
                {
                  fieldId: 32,
                  id: '47'
                }
              ]
            }
          ]
        },
        {
          id: 28,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #B',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '38',
              name: 'B1',
              code: 'B1',
              childItems: [
                {
                  fieldId: 27,
                  id: '36'
                },
                {
                  fieldId: 27,
                  id: '37'
                },
                {
                  fieldId: 31,
                  id: '44'
                },
                {
                  fieldId: 32,
                  id: '46'
                }
              ]
            },
            {
              id: '39',
              name: 'B2',
              code: 'B2',
              childItems: [
                {
                  fieldId: 27,
                  id: '37'
                },
                {
                  fieldId: 31,
                  id: '45'
                },
                {
                  fieldId: 32,
                  id: '46'
                },
                {
                  fieldId: 32,
                  id: '47'
                }
              ]
            },
            {
              id: '49',
              name: 'B3',
              code: 'B3',
              childItems: [
                {
                  fieldId: 32,
                  id: '48'
                }
              ]
            }
          ]
        },
        {
          id: 31,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #C',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '44',
              name: 'C1',
              code: 'C1',
              childItems: [
                {
                  fieldId: 27,
                  id: '36'
                }
              ]
            },
            {
              id: '45',
              name: 'C2',
              code: 'C2',
              childItems: [
                {
                  fieldId: 27,
                  id: '36'
                },
                {
                  fieldId: 27,
                  id: '37'
                }
              ]
            }
          ]
        },
        {
          id: 32,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #D',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '46',
              name: 'D1',
              code: 'D1',
              childItems: []
            },
            {
              id: '47',
              name: 'D2',
              code: 'D2',
              childItems: []
            },
            {
              id: '48',
              name: 'D3',
              code: 'D3',
              childItems: []
            }
          ]
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 8) {
      return of([
        {
          id: 29,
          character_limit: 50,
          mandatory: true,
          hidden: false,
          name: 'Dropdown field #A',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '40',
              name: 'A1',
              code: 'A1',
              childItems: [
                {
                  fieldId: 30,
                  id: '42'
                }
              ]
            },
            {
              id: '41',
              name: 'A2',
              code: 'A2',
              childItems: [
                {
                  fieldId: 30,
                  id: '43'
                }
              ]
            }
          ]
        },
        {
          id: 30,
          character_limit: 50,
          mandatory: true,
          hidden: false,
          name: 'Dropdown field #B',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '42',
              name: 'B1',
              code: 'B1',
              childItems: [
                {
                  fieldId: 29,
                  id: '41'
                }
              ]
            },
            {
              id: '43',
              name: 'B2',
              code: 'B2',
              childItems: [
                {
                  fieldId: 29,
                  id: '40'
                }
              ]
            }
          ]
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 9) {
      return of([
        {
          id: 14,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'MDMData field',
          field_desc: 'MDMData field description',
          field_type: FieldType.MDMData,
          delimiter_value: '_',
          mdm_entity: ''
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 10) {
      return of([
        {
          id: 22,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #A',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '25',
              name: 'A1',
              code: 'A1',
              childItems: [
                {
                  fieldId: 23,
                  id: '27'
                },
                {
                  fieldId: 23,
                  id: '28'
                },
                {
                  fieldId: 24,
                  id: '30'
                },
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '26',
              name: 'A2',
              code: 'A2',
              childItems: [
                {
                  fieldId: 23,
                  id: '29'
                }
              ]
            }
          ]
        },
        {
          id: 11,
          character_limit: 15,
          hidden: false,
          mandatory: true,
          name: 'Text field',
          field_desc: 'Text field description',
          field_type: FieldType.Text,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 23,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #B',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '27',
              name: 'B1',
              code: 'B1',
              childItems: [
                {
                  fieldId: 24,
                  id: '30'
                }
              ]
            },
            {
              id: '28',
              name: 'B2',
              code: 'B2',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '29',
              name: 'B3',
              code: 'B3',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            }
          ]
        },
        {
          id: 11,
          character_limit: 15,
          hidden: false,
          mandatory: true,
          name: 'Text field',
          field_desc: 'Text field description',
          field_type: FieldType.Text,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 24,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #C',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '30',
              name: 'C1',
              code: 'C1',
              childItems: []
            },
            {
              id: '31',
              name: 'C2',
              code: 'C2',
              childItems: []
            }
          ]
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 11) {
      return of([
        {
          id: 25,
          taxonomy_id: 1,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'All field types',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 26,
          taxonomy_id: 2,
          character_limit: 20,
          mandatory: false,
          hidden: false,
          name: 'Common fields test',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 27,
          taxonomy_id: 5,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Simple cascading',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 29,
          taxonomy_id: 7,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Unusual cascading',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 30,
          taxonomy_id: 10,
          character_limit: 40,
          mandatory: false,
          hidden: false,
          name: 'Simple cascading with other columns',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 31,
          taxonomy_id: 11,
          character_limit: 100,
          mandatory: false,
          hidden: false,
          name: 'Taxonomy within taxonomy test',
          field_desc: 'Taxonomy field',
          field_type: FieldType.Taxonomy,
          delimiter_value: '_',
          mdm_entity: ''
        }
      ]).pipe(delay(500));
    } else if (taxonomyId === 12) {
      return of([
        {
          id: 33,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'MDM Dropdown field #A',
          field_type: FieldType.MDMData,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 34,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'MDM Dropdown field #B',
          field_type: FieldType.MDMData,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 35,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'MDM Dropdown field #C',
          field_type: FieldType.MDMData,
          delimiter_value: '_',
          mdm_entity: ''
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 13) {
      return of([
        {
          id: 36,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date [mm-dd-yyyy]',
          field_desc: 'Date field description',
          dateFormat: 'mm-dd-yyyy',
          field_type: FieldType.Date,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 37,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date [d-m-yy]',
          field_desc: 'Date field description',
          dateFormat: 'd-m-yy',
          field_type: FieldType.Date,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 38,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date [mmm dd (ddd), yy]',
          field_desc: 'Date field description',
          dateFormat: 'mmm dd (ddd), yy',
          field_type: FieldType.Date,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 39,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date [mmmm dd (dddd), yyyy]',
          field_desc: 'Date field description',
          dateFormat: 'mmmm dd (dddd), yyyy',
          field_type: FieldType.Date,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 40,
          character_limit: 10,
          mandatory: false,
          hidden: false,
          name: 'Date [mmmmm-yy]',
          field_desc: 'Date field description',
          dateFormat: 'mmmmm-yy',
          field_type: FieldType.Date,
          delimiter_value: '_',
          mdm_entity: ''
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 14) {
      return of([
        {
          id: 22,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #A',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '25',
              name: 'A1',
              code: 'A1',
              childItems: [
                {
                  fieldId: 23,
                  id: '27'
                },
                {
                  fieldId: 23,
                  id: '28'
                },
                {
                  fieldId: 24,
                  id: '30'
                },
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '26',
              name: 'A2',
              code: 'A2',
              childItems: [
                {
                  fieldId: 23,
                  id: '29'
                }
              ]
            }
          ]
        },
        {
          id: 41,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Taxonomy Id field 1',
          field_type: FieldType.TaxonomyId,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 23,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #B',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '27',
              name: 'B1',
              code: 'B1',
              childItems: [
                {
                  fieldId: 24,
                  id: '30'
                }
              ]
            },
            {
              id: '28',
              name: 'B2',
              code: 'B2',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            },
            {
              id: '29',
              name: 'B3',
              code: 'B3',
              childItems: [
                {
                  fieldId: 24,
                  id: '31'
                }
              ]
            }
          ]
        },
        {
          id: 24,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field #C',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '30',
              name: 'C1',
              code: 'C1',
              childItems: []
            },
            {
              id: '31',
              name: 'C2',
              code: 'C2',
              childItems: []
            }
          ]
        },
        {
          id: 42,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Taxonomy Id field 2',
          field_type: FieldType.TaxonomyId,
          delimiter_value: '_',
          mdm_entity: ''
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 15) {
      return of([
        {
          id: 43,
          character_limit: 50,
          mandatory: true,
          hidden: false,
          name: 'Taxonomy Id field',
          field_type: FieldType.TaxonomyId,
          delimiter_value: '_',
          mdm_entity: ''
        },
        {
          id: 44,
          character_limit: 50,
          mandatory: true,
          hidden: false,
          name: 'MDM Search field',
          field_type: FieldType.MDMSearch,
          delimiter_value: '_',
          mdm_entity: 'Search key'
        },
        {
          id: 24,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '30',
              name: 'C1',
              code: 'C1',
              childItems: []
            },
            {
              id: '31',
              name: 'C2',
              code: 'C2',
              childItems: []
            }
          ]
        },
      ]).pipe(delay(500));
    } else if (taxonomyId === 16) {
      return of([
        {
          id: 45,
          character_limit: 15,
          mandatory: true,
          hidden: false,
          name: 'Static 1',
          field_type: FieldType.Static,
          delimiter_value: '_',
          static_field_value: 'STATIC-ONE'
        },
        {
          id: 11,
          character_limit: 15,
          mandatory: true,
          hidden: false,
          name: 'Text field',
          field_desc: 'Text field description',
          field_type: FieldType.Text,
          delimiter_value: '_'
        },
        {
          id: 46,
          character_limit: 15,
          mandatory: false,
          hidden: false,
          name: 'Static 2',
          field_type: FieldType.Static,
          delimiter_value: '_',
          static_field_value: 'STATIC-TWO'
        },
        {
          id: 24,
          character_limit: 50,
          mandatory: false,
          hidden: false,
          name: 'Dropdown field',
          field_type: FieldType.Dropdown,
          delimiter_value: '_',
          dropdown_items: [
            {
              id: '30',
              name: 'C1',
              code: 'C1',
              childItems: []
            },
            {
              id: '31',
              name: 'C2',
              code: 'C2',
              childItems: []
            }
          ]
        },
        {
          id: 47,
          character_limit: 15,
          mandatory: true,
          hidden: false,
          name: 'Static 3',
          field_type: FieldType.Static,
          delimiter_value: '_',
          static_field_value: 'STATIC-THREE'
        }
      ]).pipe(delay(500));
    } else { // last 47
      return of([]).pipe(delay(1000));
    }
  }

  getMdmDropDownItems(taxonomyId: number, fieldId: number): Observable<DropDownItem[]> {
    if (taxonomyId === 1 && fieldId === 14) {
      return of([
        {
          id: '9bee78c2-cfe7-435d-8ba9-1d25237c450e',
          name: 'MDM Value 1',
          code: 'C11',
          childItems: []
        },
        {
          id: '9bee78c2-cfe7-435d-8ba9-1d25237c450a',
          name: 'MDM Value 2',
          code: 'C12',
          childItems: []
        },
        {
          id: '9bee78c2-cfe7-435c-8ba9-1d25237c450e',
          name: 'MDM Value 3',
          code: 'C13',
          childItems: []
        },
        {
          id: '9bee78c1-cfe7-435d-8ba9-1d25237c450e',
          name: 'MDM Value 4',
          code: 'C14',
          childItems: []
        },
        {
          id: '9bee78c2-cfe7-425d-8ba9-1d25237c450e',
          name: 'MDM Value 5',
          code: 'C15',
          childItems: []
        }
      ]).pipe(delay(300));
    } else if (taxonomyId === 9 && fieldId === 14) {
      return of([
        {
          id: '9bee78c2-cfe7-435d-8ba9-1d25237c410e',
          name: 'MDM Value 6',
          code: 'C116',
          childItems: []
        },
        {
          id: '9bee78c2-cfe7-435d-8ba9-1d25137c450e',
          name: 'MDM Value 7',
          code: 'C117',
          childItems: []
        }
      ]).pipe(delay(300));
    } else if (taxonomyId === 12 && fieldId === 33) {
      return of([
        {
          id: "9bee78c2cfe7435d7ba91d25237c410e",
          name: 'A1',
          code: 'A1',
          childItems: [
            {
              fieldId: 34,
              id: "9bee78c2cfe74-35d7ba91d25-236c410e"
            },
            {
              fieldId: 34,
              id: "9bee78c2cfe7435d7ba91d25237c410b"
            },
            {
              fieldId: 35,
              id: "9aee78c2cfe7435d7ba91d25237c410e"
            },
            {
              fieldId: 35,
              id: "9bee78f2cfe7435d7ba91d25237c410e"
            }
          ]
        },
        {
          id: "9bee78c2cfe7435d7ba91d25137c410e",
          name: 'A2',
          code: 'A2',
          childItems: [
            {
              fieldId: 34,
              id: "9bee77c2cfe7435d7ba91d25237c410e"
            }
          ]
        },
      ]).pipe(delay(300));
    } else if (taxonomyId === 12 && fieldId === 34) {
      return of([
        {
          id: "9bee78c2cfe74-35d7ba91d25-236c410e",
          name: 'B1',
          code: 'B1',
          childItems: [
            {
              fieldId: 35,
              id: "9aee78c2cfe7435d7ba91d25237c410e"
            }
          ]
        },
        {
          id: "9bee78c2cfe7435d7ba91d25237c410b",
          name: 'B2',
          code: 'B2',
          childItems: [
            {
              fieldId: 35,
              id: "9bee78f2cfe7435d7ba91d25237c410e"
            }
          ]
        },
        {
          id: "9bee77c2cfe7435d7ba91d25237c410e",
          name: 'B3',
          code: 'B3',
          childItems: [
            {
              fieldId: 35,
              id: "9bee78f2cfe7435d7ba91d25237c410e"
            }
          ]
        },
      ]).pipe(delay(300));
    } else if (taxonomyId === 12 && fieldId === 35) {
      return of([
        {
          id: "9aee78c2cfe7435d7ba91d25237c410e",
          name: 'C1',
          code: 'C1',
          childItems: []
        },
        {
          id: "9bee78f2cfe7435d7ba91d25237c410e",
          name: 'C2',
          code: 'C2',
          childItems: []
        }
      ]).pipe(delay(300));
    } else {
      return of([]).pipe(delay(300));
    }
  }

  getMdmSearchDropDownItems(taxonomyId: number, filter: string, entityName: string, fieldId: number): Observable<DropDownItem[]> {
    if (taxonomyId === 15 && fieldId === 44 && entityName === 'Search key') {
      return of([
        'One 1',
        'Two 2',
        'Three 3',
        'Four 4',
        'Five 5',
        'Six 6',
        'Seven 7'
      ].filter(v => v.indexOf(filter) !== -1)
        .map(v => new DropDownItem(undefined, undefined, v, v)))
        .pipe(
          delay(300));
    } else {
      return of([]).pipe(delay(300));
    }
  }

  getTaxonomyValues(clientId: number, taxonomyId: number): Observable<DropDownItem[]> {
    let taxonomyValues = this.fakeTaxonomyValuesStorage[taxonomyId];

    if (!taxonomyValues) {
      taxonomyValues = [];
    }

    return of(taxonomyValues).pipe(delay(300));
  }

  submitTaxonomyValue(clientId: number, taxonomyId: number, taxonomyValue: TaxonomyValue): Observable<SubmitResult> {
    const items = this.fakeTaxonomyValuesStorage[taxonomyId];
    const newItem = new DropDownItem(undefined,undefined, taxonomyValue.displayValue, taxonomyValue.value);

    if (items) {
      const index = this.appHelper.findIndex(items, i =>
        i.name === newItem.name &&
        i.code === newItem.code);

      if (index === -1) {
        items.push(newItem);
      } else {
        throw new Error('Cannot insert the value. The duplicate is detected.');
      }
    } else {
      this.fakeTaxonomyValuesStorage[taxonomyId] = [newItem];
    }

    return of<SubmitResult>(new SubmitResult(this.fakeTaxonomyValuesStorage[taxonomyId].length.toString(), undefined)).pipe(delay(300));
  }

  //#endregion

  //#region Helpers

  private updateAuthResult(authResult: AuthResult): void {
    const issued = new Date();
    const expires = new Date();

    expires.setSeconds(expires.getSeconds() + authResult.expires_in);

    authResult.issued_date = issued;
    authResult.expiration_date = expires;

    if (!environment.fakeData) {
      authResult.userId = new JwtHelperService().decodeToken(authResult.access_token)['username'];
    }
  }

  //#endregion
}
